import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { DashboardLayout, WelcomeCard, RecentActivityCard } from './DashboardLayout';
import { dashboardService } from '@/services/dashboardService';
import { knowledgeBaseService } from '@/services/knowledgeBaseService';
import type { EndUserDashboardStats, DashboardActivity, UserTicket } from '@/services/dashboardService';
import type { KnowledgeArticleSummary } from '@/services/knowledgeBaseService';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * End User Dashboard Component
 * 
 * Provides customer-focused interface for end users including
 * ticket submission, ticket tracking, and self-service options.
 */
export function EndUserDashboard() {
  useDocumentTitle('My Support Dashboard | SupportDesk');
  const { user } = useAuth();
  const [stats, setStats] = useState<any[]>([]);
  const [recentActivities, setRecentActivities] = useState<DashboardActivity[]>([]);
  const [myTickets, setMyTickets] = useState<UserTicket[]>([]);
  const [popularArticles, setPopularArticles] = useState<KnowledgeArticleSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [articlesError, setArticlesError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
      loadPopularArticles();
    }
  }, [user?.id]);

  const loadDashboardData = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Load all end user dashboard data in parallel
      const [statsResponse, activitiesResponse, ticketsResponse] = await Promise.all([
        dashboardService.getEndUserStats(user.id),
        dashboardService.getRecentActivities('EndUser', user.id),
        dashboardService.getUserTickets(user.id, 5)
      ]);

      // Process end user stats
      if (statsResponse.success && statsResponse.data) {
        const data = statsResponse.data;
        const formattedStats = [
          {
            label: 'My Tickets',
            value: data.myTickets.toString(),
            change: data.changes?.myTickets,
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            ),
          },
          {
            label: 'Open Tickets',
            value: data.openTickets.toString(),
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          },
          {
            label: 'Resolved This Month',
            value: data.resolvedThisMonth.toString(),
            change: data.changes?.resolvedThisMonth,
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          },
          {
            label: 'Avg Resolution Time',
            value: data.avgResolutionTime,
            change: data.changes?.avgResolutionTime,
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            ),
          },
        ];
        setStats(formattedStats);
      }

      // Process activities
      if (activitiesResponse.success) {
        setRecentActivities(activitiesResponse.data || []);
      }

      // Process user tickets
      if (ticketsResponse.success) {
        setMyTickets(ticketsResponse.data || []);
      }

    } catch (err) {
      console.error('Failed to load end user dashboard data:', err);
      setError('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPopularArticles = async () => {
    setArticlesLoading(true);
    setArticlesError(null);

    try {
      // Get popular articles, public only for end users, limit to 4 articles
      const articles = await knowledgeBaseService.getPopularArticles(4, true);
      setPopularArticles(articles);
    } catch (err) {
      console.error('Failed to load popular articles:', err);
      setArticlesError('Failed to load popular articles. Please try again later.');
    } finally {
      setArticlesLoading(false);
    }
  };

  const actions = (
    <div className="flex space-x-3">
      <Link to="/knowledge-base">
        <Button variant="outline">
          Browse Help
        </Button>
      </Link>
      <Link to="/tickets/new">
        <Button variant="primary">
          Create Ticket
        </Button>
      </Link>
    </div>
  );

  const getPriorityBadge = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return <Badge variant="error" size="sm">High</Badge>;
      case 'medium':
        return <Badge variant="warning" size="sm">Medium</Badge>;
      case 'low':
        return <Badge variant="secondary" size="sm">Low</Badge>;
      default:
        return <Badge variant="secondary" size="sm">{priority}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return <Badge variant="info" size="sm">Open</Badge>;
      case 'inprogress':
        return <Badge variant="warning" size="sm">In Progress</Badge>;
      case 'resolved':
        return <Badge variant="success" size="sm">Resolved</Badge>;
      case 'closed':
        return <Badge variant="secondary" size="sm">Closed</Badge>;
      default:
        return <Badge variant="secondary" size="sm">{status}</Badge>;
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  if (isLoading) {
    return (
      <DashboardLayout
        title="My Support Dashboard"
        subtitle="Track your tickets and get help when you need it"
        stats={[]}
        actions={actions}
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout
        title="My Support Dashboard"
        subtitle="Track your tickets and get help when you need it"
        stats={[]}
        actions={actions}
      >
        <Card title="Error">
          <div className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadDashboardData}>Retry</Button>
          </div>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="My Support Dashboard"
      subtitle="Track your tickets and get help when you need it"
      stats={stats}
      actions={actions}
    >
      {/* Welcome Section */}
      <WelcomeCard user={{ firstName: user.firstName, role: user.role }} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* My Tickets */}
        <div className="lg:col-span-2">
          <Card title="My Recent Tickets">
            <div className="p-6">
              {myTickets.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">You don't have any support tickets yet</p>
                  <Link
                    to="/tickets/new"
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Create your first ticket
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {myTickets.map((ticket) => (
                    <div key={ticket.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <Link
                              to={`/tickets/${ticket.ticketNumber || ticket.id}`}
                              className="text-sm font-medium text-gray-900 hover:text-primary-600 truncate"
                            >
                              {ticket.subject}
                            </Link>
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>#{ticket.ticketNumber || ticket.id}</span>
                            {ticket.assignedAgent && (
                              <>
                                <span>•</span>
                                <span>Assigned to {ticket.assignedAgent}</span>
                              </>
                            )}
                            <span>•</span>
                            <span>Created {ticket.created}</span>
                            <span>•</span>
                            <span>Updated {ticket.lastUpdate}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {getPriorityBadge(ticket.priority)}
                          {getStatusBadge(ticket.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
                <Link
                  to="/tickets"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  View all my tickets →
                </Link>
                <Link
                  to="/tickets/new"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Create new ticket →
                </Link>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Help */}
        <Card title="Quick Help">
          <div className="p-6">
            <div className="space-y-4">
              <Link
                to="/knowledge-base/search?q=password+reset"
                className="block p-3 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 text-primary-600">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2m-2-2a2 2 0 00-2 2m0 0a2 2 0 00-2-2M9 5a2 2 0 012 2v6a2 2 0 11-4 0V7a2 2 0 012-2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Reset Password</p>
                    <p className="text-xs text-gray-500">Change your account password</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/knowledge-base/search?q=account+settings"
                className="block p-3 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 text-primary-600">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Account Settings</p>
                    <p className="text-xs text-gray-500">Update your profile and preferences</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/knowledge-base/search?q=billing"
                className="block p-3 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 text-primary-600">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Billing & Payments</p>
                    <p className="text-xs text-gray-500">View invoices and update payment info</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/knowledge-base/search?q=mobile+app"
                className="block p-3 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 text-primary-600">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Mobile App Help</p>
                    <p className="text-xs text-gray-500">Setup and troubleshooting guides</p>
                  </div>
                </div>
              </Link>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <Link
                to="/knowledge-base"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Browse all help articles →
              </Link>
            </div>
          </div>
        </Card>

        {/* Recent Activity */}
        <RecentActivityCard 
          activities={recentActivities}
          title="My Recent Activity"
          emptyMessage="No recent activity"
        />

        {/* Popular Articles */}
        <Card title="Popular Help Articles">
          <div className="p-6">
            {articlesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-sm text-gray-500">Loading articles...</span>
              </div>
            ) : articlesError ? (
              <div className="text-center py-8">
                <p className="text-sm text-red-600 mb-4">{articlesError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadPopularArticles}
                >
                  Retry
                </Button>
              </div>
            ) : popularArticles.length === 0 ? (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No articles available yet</p>
                <Link
                  to="/knowledge-base"
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  Browse Help Articles
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {popularArticles.map((article) => (
                  <div key={article.id}>
                    <Link
                      to={`/knowledge-base/articles/${article.id}`}
                      className="block hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {article.title}
                          </p>
                          <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                            {article.categoryName && (
                              <>
                                <span>{article.categoryName}</span>
                                <span>•</span>
                              </>
                            )}
                            <span>{article.viewCount} views</span>
                            {article.ratingCount > 0 && (
                              <>
                                <span>•</span>
                                <span>{Math.round((article.rating / 5) * 100)}% helpful</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <Link
                to="/knowledge-base"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                View all articles →
              </Link>
            </div>
          </div>
        </Card>

        {/* Contact Support */}
        <Card title="Need More Help?">
          <div className="p-6">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 text-primary-600 mb-4">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Can't find what you need?
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Our support team is here to help you with any questions or issues.
              </p>
              <div className="space-y-3">
                <Link to="/tickets/new">
                  <Button variant="primary" fullWidth>
                    Create Support Ticket
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" fullWidth>
                    Contact Support
                  </Button>
                </Link>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <div className="h-2 w-2 rounded-full bg-green-400"></div>
                  <span>Avg response: 2-4 hours</span>
                </div>
                <span>•</span>
                <span>24/7 support available</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}